import TextComponent from "app_components/commons/TextComponent/TextComponent";
import PublicAppContainer from "app_components/layouts/public";
import React, {
  CSSProperties,
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import parse from 'html-react-parser';

import "./AlliancesScreen.scss"
import { Accordion, Card } from "react-bootstrap";
import { IoIosAddCircleOutline, IoIosCloseCircleOutline } from "react-icons/io";
import LandingService from "app_services/admin/landings/landingService";
import { ILanding, ILandingAlliance } from "app_types/admin/landings/landingTypes";
import moment from "moment";
import useWindowWidth from "app_hooks/useWindowWidth";
import { IoClose } from "react-icons/io5";
import AllianceFilterMovil from "./components/AllianceFilterMovil/AllianceFilterMovil";
import { CiFilter } from "react-icons/ci";

const MOVIL_BREAKPOINT = 1100

const AlliancesScreen: FC = () => {
  const [activeKey, setActiveKey] = useState<string>()
  const [landing, updateLanding] = useState<ILanding | undefined>(undefined)
  const [alliances, setAlliances] = useState<Array<ILandingAlliance>>([])
  const [currentRegionals, setCurrentRegionals] = useState<string[]>([])
  const [parentRegional, setParentRegional] = useState<ILandingAlliance['typeAgreement']>()
  const { windowWidth } = useWindowWidth()
  const [showFilter, setShowFilter] = useState(false);
  const isMovil = windowWidth && windowWidth <= MOVIL_BREAKPOINT

  const landingService = new LandingService()

  useEffect(() => {
    getLandingData()
  },[])

  useEffect(() => {
    if (landing?.landing?.alliances?.length) {
      setAlliances([...landing.landing.alliances])
    } else {
      setAlliances([])
    }
  }, [landing])

  /**
   * @INFO Obtener la data para la landing
   */
  const getLandingData = async () => {
    let httpParams: any = {
      slug: 'alliances',
      onlyActiveAlliances: true,
    }
    const landingResponse = await landingService.fetchLandingData(httpParams)
    if (landingResponse) {
      updateLanding(landingResponse)
    }
  }

  const getRegionalsByAgreement = useCallback((agreement: ILandingAlliance['typeAgreement']) => {
    const regionals = alliances
      .filter((alliance) => alliance.typeAgreement === agreement && alliance.status)
      ?.map((alliance) => alliance.regional)
    return [ ...new Set(regionals) ] as string []
  }, [alliances])

  const getAlliancesByRegional = useCallback((regionals: string[]) => {
    return [ ...alliances.filter((alliance) => !!alliance.regional && regionals.includes(alliance.regional) && !!alliance.typeAgreement && parentRegional === alliance.typeAgreement) ]
  }, [alliances, parentRegional])

  const regionalAlliances = useMemo(() =>
    getRegionalsByAgreement('alliance'),
    [getRegionalsByAgreement],
  )

  const regionalAgreement = useMemo(() =>
    getRegionalsByAgreement('agreement'),
    [getRegionalsByAgreement],
  )

  const filteredAlliances = useMemo(() =>
    getAlliancesByRegional(currentRegionals),
    [getAlliancesByRegional, currentRegionals],
  )

  useEffect(() => {
    if (regionalAgreement?.length) {
      setCurrentRegionals(regionalAgreement)
      setParentRegional('agreement')
    } else if (regionalAlliances?.length) {
      setCurrentRegionals(regionalAlliances)
      setParentRegional('alliance')
    } else {
      setCurrentRegionals([])
      setParentRegional(undefined)
    }
  }, [regionalAgreement, regionalAlliances])

  const handleClickParentMenu = (agreement: ILandingAlliance['typeAgreement']) => {
    if (agreement === 'agreement') {
      setCurrentRegionals(regionalAgreement)
      setParentRegional(agreement)
    } else if (agreement === 'alliance') {
      setCurrentRegionals(regionalAlliances)
      setParentRegional(agreement)
    }
  }

  const handleClickMenu = (regional: string, agreement: ILandingAlliance['typeAgreement']) => {
    setParentRegional(agreement)
    setCurrentRegionals([ regional ])
  }

  const isRegionalActive = useCallback((regional: string, agreement: ILandingAlliance['typeAgreement']) => {
    if (currentRegionals?.length !== 1) return false
    return currentRegionals?.includes(regional) && parentRegional === agreement
  }, [currentRegionals, parentRegional])

  return (
    <PublicAppContainer>
      <div className="alliances-screen--layout">
        <TextComponent type="title" as='h2' textTransform="none">
          Alianzas
        </TextComponent>
        <img className="alliances-screen--image" src={landing?.landing?.descriptive_training?.image} alt="" />
        <TextComponent className="alliances-screen--title" type="title">
          {landing?.landing?.descriptive_training?.title}
        </TextComponent>
        <div className="alliances-screen--two-column-text">
          {parse(landing?.landing?.descriptive_training?.content ? landing?.landing?.descriptive_training?.content : '')}
        </div>
        <button onClick={() => setShowFilter(true)} className='alliances-screen--button-filter'>Filtrar <CiFilter size={28} /></button>
        <div className={'alliances-screen--movil-filter-container ' + (showFilter ? '' : 'close')}>
          <IoClose size={23} color='var(--secondary)' cursor={'pointer'} onClick={() => setShowFilter(false)}/>
          <AllianceFilterMovil
            agreements={regionalAgreement}
            alliances={regionalAlliances}
            parentRegional={parentRegional}
            handleClickMenu={handleClickMenu}
            isRegionalActive={isRegionalActive}
            onClose={() => setShowFilter(false)}
            handleClickParentMenu={handleClickParentMenu}
          />
        </div>
        <div className="alliances-screen--container">
          <div className="alliances-screen--alliance-list">
            {!!regionalAgreement?.length && (
              <Fragment>
                <button onClick={() => handleClickParentMenu("agreement")} type="button" data-toggle="collapse" data-target="#convenioCollapse">
                  <TextComponent
                    type="text"
                    style={{ fontWeight: 700 }}
                    className={parentRegional === 'agreement' ? 'alliances-screen--menu-active' : ''}
                  >
                    CONVENIOS
                  </TextComponent>
                  <hr />
                </button>
                <div
                  className="collapse show"
                  // id="convenioCollapse" // Comment to make an always open collapse
                >
                  {regionalAgreement?.map((regional) => (
                    <TextComponent
                      key={regional}
                      type="text"
                      onClick={() => handleClickMenu(regional, 'agreement')}
                      style={{ cursor: 'pointer' }}
                      className={isRegionalActive(regional, 'agreement') ? 'alliances-screen--menu-active' : ''}
                    >
                      {regional}
                    </TextComponent>
                  ))}
                </div>
              </Fragment>
            )}
            {!!regionalAlliances?.length && (
              <Fragment>
                <button onClick={() => handleClickParentMenu("alliance")} type="button" data-toggle="collapse" data-target="#allianceCollapse">
                  <TextComponent
                    type="text"
                    style={{ fontWeight: 700 }}
                    className={parentRegional === 'alliance' ? 'alliances-screen--menu-active' : ''}
                  >
                    ALIANZAS
                  </TextComponent>
                  <hr />
                </button>
                <div
                  className="collapse show"
                  // id="allianceCollapse" // Comment to make an always open collapse
                >
                  {regionalAlliances?.map((regional) => (
                    <TextComponent
                      key={regional}
                      type="text"
                      onClick={() => handleClickMenu(regional, 'alliance')}
                      style={{ cursor: 'pointer' }}
                      className={isRegionalActive(regional, 'alliance') ? 'alliances-screen--menu-active' : ''}
                    >
                      {regional}
                    </TextComponent>
                  ))}
                </div>
              </Fragment>
            )}
          </div>
          <div className="alliances-screen--collapse-list">
          <Accordion
            activeKey={activeKey}
            onSelect={(_key) => setActiveKey(_key ? _key : undefined)}
          >
            {filteredAlliances?.map((alliance, idx) => (
              <AccordionItem
                eventKey={String(idx)}
                key={idx}
                title={alliance.name as string}
                currentKey={activeKey}
                alliance={alliance}
              />
            ))}
          </Accordion>
          </div>
        </div>
      </div>
    </PublicAppContainer>
  )
}

// ========================================================
// @INFO Item de accordion
// ========================================================
interface IAccordionItemProps {
  alliance: ILandingAlliance
  eventKey: string
  title: string
  currentKey?: string
  className?: string
  style?: CSSProperties
  id?: string
}

const AccordionItem: React.FC<IAccordionItemProps> = (props) => {

  const { alliance } = props

  return(
    <Card
      className={`accordion_item-layout ${props.className ? props.className : ''}`}
      style={props.style}
      id={props.id}
    >
      <Accordion.Toggle
        eventKey={props.eventKey}
        className='toggle-accordion-course'
      >
        <TextComponent
          type='title'
          style={{
            fontSize: '1.4rem',
            textAlign: 'left',
            textTransform: 'initial',
          }}
        >
          {props.title}
        </TextComponent>
        <img src={props.alliance.logoFile} alt="" />
        {props.currentKey === props.eventKey ?
          <IoIosCloseCircleOutline/>
        :
          <IoIosAddCircleOutline/>
        }
      </Accordion.Toggle>
      <Accordion.Collapse
        eventKey={props.eventKey}
      >
        <Card.Body
          className='collapse-accordion-course'
        >
          <div className="alliances-screen--description">
          {!!alliance?.sectional && <div>
              <TextComponent
                type="text"
                className="alliances-screen--description-title"
              >
                Seccional/Sede:
              </TextComponent>
              <TextComponent
                type="text"
              >
                { alliance.sectional }
              </TextComponent>
            </div>}
            {!!alliance?.country && <div>
              <TextComponent
                type="text"
                className="alliances-screen--description-title"
              >
                País:
              </TextComponent>
              <TextComponent
                type="text"
              >
                { alliance.country }
              </TextComponent>
            </div>}
            {!!alliance?.regional && <div>
              <TextComponent
                type="text"
                className="alliances-screen--description-title"
              >
                Regional:
              </TextComponent>
              <TextComponent
                type="text"
              >
                {alliance.regional}
              </TextComponent>
            </div>}
            {!!alliance?.agreementDescription && <div>
              <TextComponent
                type="text"
                className="alliances-screen--description-title"
              >
                Tipo de convenio:
              </TextComponent>
              <TextComponent
                type="text"
              >
                {alliance?.agreementDescription}
              </TextComponent>
            </div>}
            {!!alliance?.creationYear && <div>
              <TextComponent
                type="text"
                className="alliances-screen--description-title"
              >
                Año de creación de convenio:
              </TextComponent>
              <TextComponent
                type="text"
              >
                {moment(alliance.creationYear).format("YYYY")}
              </TextComponent>
            </div>}
            {!!alliance?.programs?.length && <div style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <TextComponent
                type="text"
                className="alliances-screen--description-title"
              >
                Programas:
              </TextComponent>
              <ul>
                {alliance?.programs?.map((program, idx) => (
                  <li key={idx}>
                    <TextComponent
                      type="text"
                    >
                      {program}
                    </TextComponent>
                  </li>
                ))}
              </ul>
            </div>}
            {!!alliance?.webSite && <div>
              <TextComponent
                type="text"
                className="alliances-screen--description-title"
              >
                Pagina web:
              </TextComponent>
              <a
                href={alliance.webSite}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--secondary)' }}
              >
                {alliance.webSite}
              </a>
            </div>}
            {!!alliance?.brochures?.length && <div style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <TextComponent
                type="text"
                className="alliances-screen--description-title"
              >
                Folletos:
              </TextComponent>
              <ul>
                {alliance.brochures.map((brochure) => (
                  <li key={brochure.unique}>
                    <a
                      href={brochure.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--secondary)' }}
                    >
                      {brochure.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>}
            {!!alliance?.contact && <div>
              <TextComponent
                type="text"
                className="alliances-screen--description-title"
              >
                Contacto:
              </TextComponent>
              <TextComponent
                type="text"
              >
                {alliance.contact}
              </TextComponent>
            </div>}
            {!!alliance?.observations && <div>
              <TextComponent
                type="text"
                className="alliances-screen--description-title"
              >
                Observaciones:
              </TextComponent>
              <TextComponent
                type="text"
              >
                {alliance.observations}
              </TextComponent>
            </div>}
          </div>
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
}

export default AlliancesScreen
